import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { Avatar } from "components";
import { RoleComponentGuard } from "components/guards";
import { ActionRoleGuard } from "components/guards/Action-role.guard";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useHistory } from "react-router-dom";
import { useFormatDateHook, useNavigationHooks } from "hooks";
import { useTaskManageActions, useViewActions } from "store";
import { TaskPreview, TaskStatus } from "store/types";
import { apiClient } from "services/api";
import { RoleName } from "constants";

const COLORS_STATUS: Record<TaskStatus, string> = {
  open: "#e0e0e0",
  "in-progress": "#b2d6f8",
  done: "#b2eaad",
};

const ITEMS_PER_PAGE = 10;

interface Props {
  clientId: number;
  clientName: string;
  role: string | null;
}

export const ClientTasksTab: React.FC<Props> = ({ clientId, clientName, role }) => {
  const history = useHistory();
  const { setCreateAction, updateTaskStatusAction } = useTaskManageActions();
  const { setIsDealCharBarOpenAction } = useViewActions();
  const { setParams } = useNavigationHooks();
  const formatDate = useFormatDateHook();
  // Local state for tasks, search and pagination
  const [tasks, setTasks] = useState<TaskPreview[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [activeStatusFilter, setActiveStatusFilter] = useState<TaskStatus | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isTeamViewer = role === RoleName.TeamViewer;

  const fetchClientTasks = useCallback(async (search?: string, status?: TaskStatus | null) => {
    setLoading(true);
    setError(false);
    try {
      const params: Record<string, any> = { per_page: 100 };
      if (search) params.search = search;
      if (status) params.status = status;
      console.log("Fetching tasks with params:", apiClient.baseURL);
      const response = await apiClient.get<{ data: TaskPreview[]; meta: object }>(
        `/api/v0.0.3/clients/${clientId}/tasks`,
        params
      );
      // Safely extract the tasks array — handle both wrapped and unwrapped shapes defensively
      const tasksArray: TaskPreview[] = response.data?.data ?? (Array.isArray(response.data) ? response.data : []);
      setTasks(tasksArray);
    } catch (err) {
      setError(true);
      setTasks([]); // ensure tasks is always an array — never undefined
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const sortedTasks = useMemo(() => {
    // Defensive: if store was contaminated by another component's fetch,
    // we cannot reliably filter by clientId since TaskPreview has no client_id field.
    // We sort and display whatever the last fetch returned.
    // The entity_type=client&entity_id=clientId query params on our fetch ensure correctness.
    return [...tasks].sort((a, b) => {
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(b.due_date).getTime() - new Date(a.due_date).getTime();
    });
  }, [tasks]);

  /**
   * Determine the next status in the cycle: open → in-progress → done → open.
   */
  const getNextStatus = (current: TaskStatus): TaskStatus => {
    if (current === "open") return "in-progress";
    if (current === "in-progress") return "done";
    return "open";
  };

  /**
   * Re-fetch tasks with the given search term, debounced at 300ms.
   */
  const debouncedSearch = useCallback(
    (value: string) => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = setTimeout(() => {
        setCurrentPage(1);
        fetchClientTasks(value || undefined, activeStatusFilter);
      }, 300);
    },
    [clientId, fetchClientTasks, activeStatusFilter],
  );

  /**
   * Handle search input changes with debounce.
   */
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInput(value);
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  /**
   * Handle status filter click.
   */
  const handleStatusFilter = useCallback(
    (status: TaskStatus) => {
      const newFilter = activeStatusFilter === status ? null : status;
      setActiveStatusFilter(newFilter);
      setCurrentPage(1);
      fetchClientTasks(searchInput || undefined, newFilter);
    },
    [activeStatusFilter, clientId, fetchClientTasks, searchInput],
  );

  /**
   * Clear all filters and search.
   */
  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setActiveStatusFilter(null);
    setCurrentPage(1);
    fetchClientTasks();
  }, [clientId, fetchClientTasks]);

  const handleAddTask = useCallback(() => {
    setCreateAction({
      isOpen: true,
      entity_type: "client" as any,
      entity_id: clientId,
      disableRedirectOnSubmit: true,
    });
  }, [setCreateAction, clientId]);

  /**
   * Toggle task status with optimistic update and revert on failure.
   */
  const handleStatusToggle = useCallback(
    async (taskId: number, currentStatus: TaskStatus) => {
      const nextStatus = getNextStatus(currentStatus);
      try {
        await updateTaskStatusAction({ id: taskId, status: nextStatus });
        // Re-fetch after successful update to sync with server state.
        fetchClientTasks(searchInput || undefined, activeStatusFilter);
      } catch {
        // On failure, re-fetch to ensure UI reflects server state.
        fetchClientTasks(searchInput || undefined, activeStatusFilter);
      }
    },
    [updateTaskStatusAction, fetchClientTasks, clientId, searchInput, activeStatusFilter],
  );

  // Fetch tasks on mount
  useEffect(() => {
    fetchClientTasks();
  }, [fetchClientTasks]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const hasFilters = activeStatusFilter !== null || searchInput !== "";

  return (
    <Stack sx={{ gap: "16px", width: "100%", height: "100%" }}>
      {/* Header */}
      <Typography
        variant="h6"
        sx={{ fontSize: "18px", color: "#071F35", fontWeight: 500 }}
      >
        Client Tasks
      </Typography>

      {/* White panel wrapper for filter bar + task list */}
      <Stack
        sx={{
          bgcolor: "#fff",
          borderRadius: "16px",
          boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
          p: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          flexGrow: 1,
          overflow: "hidden",
        }}
      >
        {/* Filter bar: search input + status filter buttons */}
        {/* Filter bar: search input + status filter buttons */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          sx={{ width: "100%", gap: "16px" }}
        >
        {/* Left side elements: Search and Filters */}
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          sx={{ gap: "8px" }}
        >
          {/* Search input */}
          <OutlinedInput
            size="small"
            placeholder="Search tasks..."
            value={searchInput}
            onChange={handleSearchChange}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: "18px", color: "#898989" }} />
              </InputAdornment>
            }
            sx={{
              fontSize: "13px",
              height: "32px",
              width: "220px",
              ".MuiOutlinedInput-notchedOutline": { borderColor: "#dae0e2" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#0a3142" },
            }}
          />

          {/* Status filter label */}
          <Typography
            variant="body2"
            sx={{ color: "#071F35", fontWeight: 500, fontSize: "13px" }}
          >
            Filter:
          </Typography>
          {(["open", "in-progress", "done"] as TaskStatus[]).map((status) => (
            <Button
              key={status}
              size="small"
              variant={activeStatusFilter === status ? "contained" : "outlined"}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                px: 1.5,
                py: 0.5,
                minWidth: "auto",
                borderColor: COLORS_STATUS[status],
                color: activeStatusFilter === status ? "#fff" : COLORS_STATUS[status],
                bgcolor: activeStatusFilter === status ? COLORS_STATUS[status] : "transparent",
                "&:hover": {
                  bgcolor: activeStatusFilter === status ? COLORS_STATUS[status] : "#f5f7f7",
                },
              }}
              onClick={() => handleStatusFilter(status)}
            >
              {status === "open"
                ? "Open"
                : status === "in-progress"
                  ? "In Progress"
                  : "Done"}
            </Button>
          ))}

          {/* Clear all filters button */}
          {hasFilters && (
            <Button
              size="small"
              onClick={handleClearFilters}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                color: "#898989",
                "&:hover": { color: "#071F35" },
              }}
            >
              Clear
            </Button>
          )}
        </Stack>

        {/* Right side element: Add Task button */}
        <Box>
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
            <Button
              variant="contained"
              onClick={handleAddTask}
              startIcon={<AddIcon />}
              sx={{
                height: "27px",
                fontSize: "14px",
                textTransform: "none",
                fontWeight: 400,
                bgcolor: "#071F35",
                "&:hover": { bgcolor: "#0a3142" },
              }}
            >
              Add Task
            </Button>
          </RoleComponentGuard>
        </Box>
      </Stack>

      {/* Task list area */}
      {loading ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ py: 4 }}
        >
          <CircularProgress size={24} />
        </Stack>
      ) : error ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            py: 4,
            gap: 1,
            border: "1px solid #ffcdd2",
            borderRadius: "8px",
            bgcolor: "#fff5f5",
          }}
        >
          <Typography sx={{ color: "#d32f2f", fontSize: "14px" }}>
            Could not load tasks. Please try again.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => {
              fetchClientTasks(searchInput || undefined, activeStatusFilter);
            }}
            sx={{ textTransform: "none", fontSize: "12px" }}
          >
            Retry
          </Button>
        </Stack>
      ) : sortedTasks.length > 0 ? (
        <Stack sx={{ flexGrow: 1, overflow: "hidden" }}>
          <TableContainer
            sx={{ borderRadius: "10px", border: "1px solid #dae0e2", height: "100%", overflow: "auto" }}
          >
            <Table sx={{ minWidth: 900, color: "#0a3142" }}>
              {/* Table header */}
              <TableHead>
                <TableRow sx={{ backgroundColor: "#edf1f9" }}>
                  <TableCell
                    sx={{
                      fontSize: "15px",
                      fontWeight: "normal",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      padding: "0 20px",
                      color: "#0a3142",
                      height: "50px",
                      width: "30%",
                    }}
                  >
                    Title
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "15px",
                      fontWeight: "normal",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      padding: "0 20px",
                      color: "#0a3142",
                      height: "50px",
                      width: "25%",
                    }}
                  >
                    Deal
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "15px",
                      fontWeight: "normal",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      padding: "0 20px",
                      color: "#0a3142",
                      height: "50px",
                      width: "10%",
                    }}
                  >
                    Assignee
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "15px",
                      fontWeight: "normal",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      padding: "0 20px",
                      color: "#0a3142",
                      height: "50px",
                      width: "10%",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "15px",
                      fontWeight: "normal",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      padding: "0 20px",
                      color: "#0a3142",
                      height: "50px",
                      width: "10%",
                    }}
                  >
                    Priority
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "15px",
                      fontWeight: "normal",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      padding: "0 20px",
                      color: "#0a3142",
                      height: "50px",
                      width: "10%",
                    }}
                  >
                    Due Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "15px",
                      fontWeight: "normal",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      padding: "0 20px",
                      color: "#0a3142",
                      height: "50px",
                      width: "5%",
                    }}
                  >
                  </TableCell>
                </TableRow>
              </TableHead>

              {/* Table body */}
              <TableBody>
                {sortedTasks
                  .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
                  .map((task) => {
                    const statusLabel =
                      task.status === "open"
                        ? "Open"
                        : task.status === "in-progress"
                          ? "In Progress"
                          : "Done";
                    const priorityLabel = task.priority
                      ? task.priority.charAt(0).toUpperCase() + task.priority.slice(1)
                      : "-";
                    return (
                      <TableRow
                        key={task.id}
                        sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
                        onClick={() => history.push(`/main/task_management?task=${task.id}`)}
                      >
                        {/* Title */}
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            padding: "0 20px",
                            color: "#0a3142",
                            fontSize: "16px",
                            height: "50px",
                            cursor: "pointer",
                          }}
                        >
                          <MaterialTooltip placement="top" title={task.title}>
                            <Typography noWrap>{task.title}</Typography>
                          </MaterialTooltip>
                        </TableCell>

                        {/* Deal column */}
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            padding: "0 20px",
                            color: "#0a3142",
                            fontSize: "16px",
                            height: "50px",
                            cursor: "pointer",
                          }}
                        >
                          <MaterialTooltip
                            placement="top"
                            title={task.deal?.title || clientName}
                          >
                            <Typography noWrap>
                              {task.deal ? task.deal.title : clientName}
                            </Typography>
                          </MaterialTooltip>
                        </TableCell>

                        {/* Assignee */}
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            padding: "0 20px",
                            color: "#0a3142",
                            fontSize: "16px",
                            height: "50px",
                            cursor: "pointer",
                          }}
                        >
                          {task.assignee ? (
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap="10px"
                            >
                              <Avatar
                                size={23}
                                src={task.assignee.profile_photo_url || ""}
                              />
                              <MaterialTooltip
                                placement="top"
                                title={task.assignee.full_name}
                              >
                                <Typography noWrap sx={{ fontSize: "16px" }}>
                                  {task.assignee.full_name}
                                </Typography>
                              </MaterialTooltip>
                            </Stack>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: "16px",
                                color: "#898989",
                                fontStyle: "italic",
                              }}
                            >
                              Unassigned
                            </Typography>
                          )}
                        </TableCell>

                        {/* Status chip */}
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            padding: "0 20px",
                            color: "#0a3142",
                            fontSize: "16px",
                            height: "50px",
                            cursor: "pointer",
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                              width: "96px",
                              height: "21px",
                              fontSize: "16px",
                              color: "#013951",
                              borderRadius: "4px",
                              bgcolor: COLORS_STATUS[task.status],
                            }}
                          >
                            {statusLabel}
                          </Stack>
                        </TableCell>

                        {/* Priority chip */}
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            padding: "0 20px",
                            color: "#0a3142",
                            fontSize: "16px",
                            height: "50px",
                            cursor: "pointer",
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            gap="5px"
                          >
                            <Box
                              sx={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "2px",
                                bgcolor:
                                  task.priority === "high"
                                    ? "#fad07c"
                                    : task.priority === "low"
                                      ? "#c5da7d"
                                      : "rgba(38, 166, 174, 0.45)",
                              }}
                            />
                            <Typography sx={{ fontSize: "16px", color: "#013951" }}>
                              {priorityLabel}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {/* Due Date */}
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            padding: "0 20px",
                            color: task.is_overdue ? "#ff0000" : "#0a3142",
                            fontSize: "16px",
                            height: "50px",
                            cursor: "pointer",
                          }}
                        >
                          {task.due_date ? formatDate({ date: task.due_date }) : "-"}
                        </TableCell>

                        {/* Status toggle */}
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            padding: "0 20px",
                            color: "#0a3142",
                            fontSize: "16px",
                            height: "50px",
                            cursor: "pointer",
                          }}
                        >
                          <ActionRoleGuard
                            availableRoles={[RoleName.Admin, RoleName.Analyst]}
                          >
                            <CheckCircleOutlineIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusToggle(task.id, task.status);
                              }}
                              sx={{
                                width: "20px",
                                height: "20px",
                                color: COLORS_STATUS[task.status],
                                cursor: "pointer",
                                "&:hover": { opacity: 0.8 },
                              }}
                            />
                          </ActionRoleGuard>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {sortedTasks.length > ITEMS_PER_PAGE && (
            <Stack alignItems="center" sx={{ mt: 2 }}>
              <Pagination
                count={Math.ceil(sortedTasks.length / ITEMS_PER_PAGE)}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                size="small"
                sx={{
                  ".MuiPaginationItem-root": {
                    fontSize: "13px",
                    color: "#0a3142",
                  },
                  ".Mui-selected": {
                    bgcolor: "#071F35 !important",
                    color: "#fff",
                  },
                }}
              />
            </Stack>
          )}
        </Stack>
      ) : hasFilters ? (
        /* Empty filtered state */
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ py: 4, gap: 1 }}
        >
          <Typography sx={{ color: "#898989", fontSize: "14px" }}>
            No tasks match filters
          </Typography>
          <Button
            size="small"
            onClick={handleClearFilters}
            sx={{ textTransform: "none", fontSize: "12px", color: "#071F35" }}
          >
            Clear all filters
          </Button>
        </Stack>
      ) : (
        /* S-3: Empty state */
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ py: 4 }}
        >
          <Typography sx={{ color: "#898989", fontSize: "14px" }}>
            No tasks for this client yet.
          </Typography>
        </Stack>
      )}
      </Stack>
    </Stack>
  );
};
